import { c as _c } from "react/compiler-runtime";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { UserDisplay } from "@components/common/user-display";
import { useRefreshSeatUsage } from "@components/table/members/assign-seats/use-refresh-seat-usage";
import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { isSuccessResponse } from "@context-providers/error-boundary/error-boundary-utils";
import { Alert, Dialog, CloseIcon, Text, useToast, Tooltip } from "@flat-ui";
import { useOnGroupRemoved } from "@hooks/use-on-group-removed";
import { fetchingGroupsFlagsSelector } from "@store/groups/groups-selector";
import { removeMemberFromGroup } from "@store/groups/groups-slice-thunk";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { removeMembersByEmails } from "@store/teams/teams-slice";
import { GroupMemberEvents, ProjectMemberEvents, TableType, WorkspaceMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useMemo, useState } from "react";

/** Generic prop for RemoveSingleMemberFromEntity */

/** Props for removing a single member from project */

/** Props for removing a single member from group */

/** Props for removing a single member from company */

/**
 * Opens a dialog with the member to be removed from project or group.
 */
export function RemoveSingleMemberFromEntity(t0) {
  const $ = _c(58);
  const {
    companyId,
    member,
    entity,
    entityName,
    isOpenOnMount: t1,
    onClose
  } = t0;
  const isOpenOnMount = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  let t2;
  if ($[0] !== companyId || $[1] !== entity.name) {
    t2 = {
      companyId,
      groupName: entity.name
    };
    $[0] = companyId;
    $[1] = entity.name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onGroupRemoved = useOnGroupRemoved(t2);
  const {
    trackEvent
  } = useTrackEvent();
  const [isOpen, setIsOpen] = useState(isOpenOnMount);
  let t3;
  if ($[3] !== onClose) {
    t3 = function closeDialog() {
      setIsOpen(false);
      onClose?.();
    };
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const closeDialog = t3;
  const {
    isRemovingProjectMember
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const {
    isRemovingGroupMember
  } = useAppSelector(fetchingGroupsFlagsSelector);
  const {
    isRemovingCompanyMember
  } = useAppSelector(fetchingMembersFlagsSelector);
  const refreshSeatUsage = useRefreshSeatUsage();
  let t4;
  bb0: {
    switch (entityName) {
      case "project":
        {
          t4 = isRemovingProjectMember;
          break bb0;
        }
      case "group":
        {
          t4 = isRemovingGroupMember;
          break bb0;
        }
      case "workspace":
      case "activeMembers":
        {
          t4 = isRemovingCompanyMember;
          break bb0;
        }
    }
    t4 = undefined;
  }
  const isProcessing = t4;
  const entityDisplayName = entityName === "activeMembers" ? "active members" : entityName;
  const title = `Remove from ${entityDisplayName}`;
  let t5;
  if ($[5] !== entity.name || $[6] !== entityName || $[7] !== member) {
    t5 = <RemoveMemberSuccessDescription member={member} subjectType={entityName} entityName={entity.name} />;
    $[5] = entity.name;
    $[6] = entityName;
    $[7] = member;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const successDescription = t5;
  let t6;
  if ($[9] !== companyId || $[10] !== dispatch || $[11] !== entity.id || $[12] !== member || $[13] !== trackEvent) {
    t6 = async function removeProjectMember() {
      trackEvent({
        name: ProjectMemberEvents.removeMember,
        props: {
          dataType: TableType.projectMembers
        }
      });
      const result = await dispatch(removeMemberFromProject({
        companyId,
        member,
        projectId: entity.id
      }));
      return result;
    };
    $[9] = companyId;
    $[10] = dispatch;
    $[11] = entity.id;
    $[12] = member;
    $[13] = trackEvent;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const removeProjectMember = t6;
  let t7;
  if ($[15] !== companyId || $[16] !== dispatch || $[17] !== entityName || $[18] !== member || $[19] !== refreshSeatUsage || $[20] !== trackEvent) {
    t7 = async function removeCompanyMember() {
      trackEvent({
        name: WorkspaceMemberEvents.removeMember,
        props: {
          dataType: entityName === "workspace" ? TableType.companyMembers : TableType.activeMembers
        }
      });
      return dispatch(removeMemberFromCompany({
        companyId,
        member
      })).unwrap().then(response => {
        refreshSeatUsage();
        return dispatch(removeMembersByEmails([response.email]));
      });
    };
    $[15] = companyId;
    $[16] = dispatch;
    $[17] = entityName;
    $[18] = member;
    $[19] = refreshSeatUsage;
    $[20] = trackEvent;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const removeCompanyMember = t7;
  let t8;
  if ($[22] !== companyId || $[23] !== dispatch || $[24] !== entity.id || $[25] !== member || $[26] !== onGroupRemoved || $[27] !== trackEvent) {
    t8 = async function removeGroupMember() {
      trackEvent({
        name: GroupMemberEvents.removeMember,
        props: {
          dataType: TableType.groupMembers
        }
      });
      const result_0 = await dispatch(removeMemberFromGroup({
        companyId,
        member,
        groupId: entity.id,
        onGroupRemoved
      }));
      return result_0;
    };
    $[22] = companyId;
    $[23] = dispatch;
    $[24] = entity.id;
    $[25] = member;
    $[26] = onGroupRemoved;
    $[27] = trackEvent;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const removeGroupMember = t8;
  let t9;
  if ($[29] !== closeDialog || $[30] !== entityName || $[31] !== openToast || $[32] !== removeCompanyMember || $[33] !== removeGroupMember || $[34] !== removeProjectMember || $[35] !== successDescription) {
    t9 = async function removeMember() {
      let result_1;
      bb38: switch (entityName) {
        case "project":
          {
            result_1 = await removeProjectMember();
            break bb38;
          }
        case "group":
          {
            result_1 = await removeGroupMember();
            break bb38;
          }
        case "workspace":
        case "activeMembers":
          {
            result_1 = await removeCompanyMember();
          }
      }
      if (isSuccessResponse(result_1)) {
        openToast({
          title: `Member removed from ${entityName}`,
          message: successDescription
        });
      }
      closeDialog();
    };
    $[29] = closeDialog;
    $[30] = entityName;
    $[31] = openToast;
    $[32] = removeCompanyMember;
    $[33] = removeGroupMember;
    $[34] = removeProjectMember;
    $[35] = successDescription;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const removeMember = t9;
  let t10;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      textAlign: "right",
      visibility: "hidden"
    };
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SphereIconButton aria-label="more" aria-haspopup="true" component={CloseIcon} onClick={() => setIsOpen(true)} />;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== title) {
    t12 = <Tooltip component="div" title={title} sx={t10} data-testid="REMOVE_USER_BUTTON" className={SHOW_ON_HOVER_CLASS}>{t11}</Tooltip>;
    $[39] = title;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== entity.members || $[42] !== entityDisplayName || $[43] !== entityName) {
    t13 = entityName === "group" && entity.members.length === 1 ? <Alert variant="warning" title={<>Removing <b>the last group member</b> will also delete the group if there are no projects assigned.</>} sx={{
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    }} /> : <Text variant="bodyM" sx={{
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    }}>When removing member from {entityDisplayName} they will no longer be able to access this {entityDisplayName}. You can always invite this member again.</Text>;
    $[41] = entity.members;
    $[42] = entityDisplayName;
    $[43] = entityName;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== member) {
    t14 = <UserDisplay member={member} />;
    $[45] = member;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== closeDialog || $[48] !== isOpen || $[49] !== isProcessing || $[50] !== removeMember || $[51] !== t13 || $[52] !== t14 || $[53] !== title) {
    t15 = <Dialog title={title} confirmText="Remove" open={isOpen} onConfirm={removeMember} onCancel={closeDialog} showSpinner={isProcessing}>{t13}{t14}</Dialog>;
    $[47] = closeDialog;
    $[48] = isOpen;
    $[49] = isProcessing;
    $[50] = removeMember;
    $[51] = t13;
    $[52] = t14;
    $[53] = title;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t12 || $[56] !== t15) {
    t16 = <>{t12}{t15}</>;
    $[55] = t12;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  return t16;
}